import { type ToolSpec, n, s, fmt, money } from "./types";

export const MONEY_TOOLS: ToolSpec[] = [
  {
    slug: "escooter-range-calculator",
    cluster: "lifestyle",
    name: "E-Scooter Range Estimator",
    h1: "Electric Scooter Battery Range Calculator: Rider Weight & Speed",
    title: "Electric Scooter Range Calculator (Wh, Weight, Hills) 2026",
    description:
      "Estimate real-world electric scooter range from battery watt-hours, rider weight, riding mode and terrain. Get miles, km and Wh-per-mile efficiency instantly.",
    keywords: [
      "real world electric scooter range estimator",
      "e-scooter battery wh to miles calculator",
      "rider weight vs battery range",
      "how many miles will a 500wh scooter go",
      "electric scooter wh per mile efficiency",
      "does rider weight reduce scooter range",
    ],
    inputs: [
      { id: "volts", label: "Battery voltage", type: "number", def: "48", unit: "V", min: 12, step: 1 },
      { id: "ah", label: "Battery capacity", type: "number", def: "15", unit: "Ah", min: 1, step: 0.5 },
      { id: "watts", label: "Motor power", type: "number", def: "600", unit: "W", min: 100, step: 50 },
      { id: "weight", label: "Rider weight", type: "number", def: "160", unit: "lbs", min: 60, step: 1 },
      {
        id: "mode", label: "Riding mode", type: "select", def: "normal",
        options: [
          { value: "eco", label: "Eco (low speed, gentle throttle)" },
          { value: "normal", label: "Normal (mixed city riding)" },
          { value: "sport", label: "Sport (full throttle, top speed)" },
        ],
      },
      {
        id: "terrain", label: "Terrain", type: "select", def: "mixed",
        options: [
          { value: "flat", label: "Flat smooth pavement" },
          { value: "mixed", label: "Mixed city with light hills" },
          { value: "hilly", label: "Hilly / steep inclines" },
        ],
      },
    ],
    compute: (v) => {
      const wh = n(v, "volts", 48) * n(v, "ah", 15);
      const watts = n(v, "watts", 600);
      const lbs = n(v, "weight", 160);
      const modeF = { eco: 0.85, normal: 1, sport: 1.3 }[s(v, "mode", "normal")] ?? 1;
      const terrainF = { flat: 0.9, mixed: 1.1, hilly: 1.45 }[s(v, "terrain", "mixed")] ?? 1.1;
      const weightF = 1 + (lbs - 160) / 300;
      const powerF = 1 + Math.max(0, watts - 500) / 4000;
      const whPerMile = 20 * weightF * terrainF * modeF * powerF;
      const usable = wh * 0.85;
      const miles = whPerMile > 0 ? usable / whPerMile : 0;
      const km = miles * 1.60934;
      return {
        cards: [
          { label: "Real-world range", value: `${fmt(miles, 1)} mi`, sub: `${fmt(km, 1)} km on 85% usable pack`, accent: true },
          { label: "Efficiency", value: `${fmt(whPerMile, 1)} Wh/mi`, sub: `${fmt(whPerMile / 1.60934, 1)} Wh/km` },
          { label: "Battery size", value: `${fmt(wh, 0)} Wh`, sub: `${fmt(usable, 0)} Wh usable` },
          { label: "Weight penalty", value: `${fmt((weightF - 1) * 100, 0)}%`, sub: "Versus a 160 lb reference rider" },
        ],
        summary: `At ${fmt(lbs, 0)} lbs on ${s(v, "terrain", "mixed")} terrain in ${s(v, "mode", "normal")} mode, expect roughly ${fmt(miles, 1)} miles (${fmt(km, 1)} km) per charge — about ${fmt(whPerMile, 1)} Wh per mile.`,
        table: {
          title: "Range at different riding modes",
          head: ["Mode", "Wh per mile", "Range (mi)", "Range (km)"],
          rows: (["eco", "normal", "sport"] as const).map((m) => {
            const f = { eco: 0.85, normal: 1, sport: 1.3 }[m];
            const w = 20 * weightF * terrainF * f * powerF;
            const mi = usable / w;
            return [m, fmt(w, 1), fmt(mi, 1), fmt(mi * 1.60934, 1)];
          }),
          caption: "Same battery and rider, only the throttle discipline changes.",
        },
        bars: (["flat", "mixed", "hilly"] as const).map((t) => {
          const f = { flat: 0.9, mixed: 1.1, hilly: 1.45 }[t];
          const mi = usable / (20 * weightF * f * modeF * powerF);
          return { label: t, value: mi, display: `${fmt(mi, 1)} mi` };
        }),
      };
    },
    intro:
      "Manufacturer range claims are measured with a light rider, flat ground and a crawling constant speed, which is why the number on the box rarely matches the number on your commute. This calculator rebuilds the estimate around your actual battery watt-hours, body weight, throttle habits and the hills you really ride.",
    sections: [
      { h: "Why watt-hours, not amp-hours, decide range", body: "Amp-hours only describe charge; watt-hours describe energy. A 36V 10Ah pack (360 Wh) and a 48V 10Ah pack (480 Wh) look identical on a spec sheet line but the second one carries a third more energy. Always multiply voltage by amp-hours before comparing two scooters." },
      { h: "How rider weight actually eats range", body: "Every extra pound increases rolling resistance and the energy needed to accelerate away from every stop. The weight factor used here adds roughly one percent of energy consumption for each three pounds above a 160 lb reference rider, which tracks closely with real GPS logs from commuter riders." },
      { h: "Hills are the single biggest variable", body: "Climbing burns energy proportional to weight times height gained, and regenerative braking recovers only a fraction of it on the way down. A route with repeated steep climbs can cut usable range by 30-40% compared with the same distance on flat pavement." },
      { h: "Why only 85% of the pack is usable", body: "Battery management systems cut off before the cells are fully drained to protect longevity, and voltage sag under load means the last stretch of the pack delivers less usable power. Planning around 85% usable capacity keeps you from stranding yourself." },
      { h: "Temperature, tyres and speed", body: "Cold weather below 5°C can temporarily reduce capacity by 10-20%, under-inflated tyres add rolling resistance, and aerodynamic drag rises with the square of speed — which is why sport mode costs far more than the extra few mph suggest." },
    ],
    staticTable: {
      title: "Typical scooter battery sizes and realistic ranges",
      head: ["Battery", "Watt-hours", "Typical real range", "Class"],
      rows: [
        ["36V 7.8Ah", "281 Wh", "8–12 mi", "Budget commuter"],
        ["36V 10.4Ah", "374 Wh", "11–16 mi", "Entry commuter"],
        ["48V 13Ah", "624 Wh", "18–26 mi", "Mid performance"],
        ["48V 20Ah", "960 Wh", "28–40 mi", "Long-range commuter"],
        ["52V 23Ah", "1196 Wh", "34–50 mi", "Enthusiast dual motor"],
        ["60V 30Ah", "1800 Wh", "45–70 mi", "Heavy performance"],
      ],
      caption: "Ranges assume mixed terrain and a rider around 160–200 lbs.",
    },
    faqs: [
      { q: "How do I find my scooter's watt-hours?", a: "Multiply the battery voltage by the amp-hour rating printed on the deck sticker or in the manual. A 48V 15Ah pack is 720 Wh." },
      { q: "Why is my real range half the advertised range?", a: "Advertised figures typically use a 155 lb rider at a steady 15 km/h on flat ground in eco mode. Real commuting with stops, hills and full throttle can easily double energy consumption per mile." },
      { q: "Does a bigger motor reduce range?", a: "Only indirectly. A larger motor lets you draw more power, and riders usually use it. At identical speeds a big motor can even be slightly more efficient because it runs further from its thermal limit." },
      { q: "How much range do I lose per extra 50 pounds?", a: "Roughly 15-17% based on the weight factor used here, and more if your route includes climbs, since hill energy scales directly with total mass." },
      { q: "Does regenerative braking add meaningful range?", a: "In stop-and-go city riding it typically recovers 3-8% of energy. On flat, steady routes the benefit is close to zero." },
      { q: "Should I ride in eco mode to maximise range?", a: "Yes — eco mode caps speed and throttle ramp, and because drag rises with the square of speed, dropping from 25 mph to 15 mph can extend range by 30% or more." },
      { q: "How does cold weather affect the estimate?", a: "Subtract about 10-20% from the calculated range in near-freezing temperatures, and store the battery indoors so it starts each ride warm." },
      { q: "Is it bad to run the battery to zero?", a: "Repeated deep discharges accelerate cell ageing. Charging back up when you reach 20-30% remaining meaningfully extends pack life." },
      { q: "Does tyre pressure change range?", a: "Yes. Pneumatic tyres 10 psi under spec can cost 5-10% of range through rolling resistance, and they also wear faster." },
      { q: "Can I use this calculator for e-bikes?", a: "The structure applies, but e-bikes with pedal assist consume far fewer watt-hours per mile — typically 8-15 Wh/mi rather than 20-35." },
    ],
    formula: {
      expressions: [
        "Battery_Wh = Voltage × Amp_hours",
        "Weight_Factor = 1 + ((Rider_lbs − 160) / 300)",
        "Wh_per_Mile = 20 × Weight_Factor × Terrain_Factor × Mode_Factor",
        "Range_Miles = (Battery_Wh × 0.85) / Wh_per_Mile",
      ],
      variables: [
        { name: "0.85", meaning: "Usable share of pack after BMS cutoff and voltage sag" },
        { name: "Terrain_Factor", meaning: "0.9 flat, 1.1 mixed, 1.45 hilly" },
        { name: "Mode_Factor", meaning: "0.85 eco, 1.0 normal, 1.3 sport" },
      ],
    },
    related: ["solar-string-voltage-calculator", "nas-power-cost-calculator", "kva-to-watts-calculator", "overtime-pay-calculator", "pawn-loan-apr-calculator"],
  },

  {
    slug: "airbnb-cleaning-fee-calculator",
    cluster: "finance",
    name: "Airbnb Cleaning Fee & Margin",
    h1: "Airbnb Cleaning Fee & Nightly Revenue Profit Margin Calculator",
    title: "Airbnb Cleaning Fee & Net Profit Margin Calculator (2026)",
    description:
      "Work out what to charge for an Airbnb cleaning fee and see gross booking value, turnover cost, host net profit and net margin per stay in one calculation.",
    keywords: [
      "how much to charge for airbnb cleaning fee calculator",
      "airbnb host net revenue per stay tool",
      "turnover cost calculator short term rental",
      "airbnb profit margin per booking",
      "short term rental cleaning fee pricing",
      "airbnb host service fee 3 percent calculator",
    ],
    inputs: [
      { id: "nightly", label: "Nightly rate", type: "number", def: "120", unit: "$", min: 0, step: 1 },
      { id: "nights", label: "Stay length", type: "number", def: "3", unit: "nights", min: 1, step: 1 },
      { id: "feeCharged", label: "Cleaning fee charged to guest", type: "number", def: "75", unit: "$", min: 0, step: 1 },
      { id: "cleaner", label: "What you pay the cleaner", type: "number", def: "60", unit: "$", min: 0, step: 1 },
      { id: "supplies", label: "Supplies restock per turnover", type: "number", def: "12", unit: "$", min: 0, step: 1 },
      { id: "hostFee", label: "Host platform fee", type: "number", def: "3", unit: "%", min: 0, max: 20, step: 0.1 },
    ],
    compute: (v) => {
      const nightly = n(v, "nightly", 120), nights = Math.max(1, n(v, "nights", 3));
      const feeCharged = n(v, "feeCharged", 75), cleaner = n(v, "cleaner", 60), supplies = n(v, "supplies", 12);
      const hostFee = n(v, "hostFee", 3) / 100;
      const gross = nightly * nights + feeCharged;
      const platform = gross * hostFee;
      const turnover = cleaner + supplies;
      const net = gross - platform - turnover;
      const margin = gross > 0 ? (net / gross) * 100 : 0;
      const perNight = net / nights;
      const cleaningGap = feeCharged - turnover;
      return {
        cards: [
          { label: "Gross booking value", value: money(gross, "$", 2), sub: `${nights} nights + cleaning fee` },
          { label: "Total turnover cost", value: money(turnover, "$", 2), sub: "Cleaner + supplies" },
          { label: "Host net profit", value: money(net, "$", 2), sub: `${money(perNight, "$", 2)} per night`, accent: true },
          { label: "Net margin", value: `${fmt(margin, 1)}%`, sub: cleaningGap >= 0 ? "Cleaning fee covers turnover" : "Cleaning fee under-recovers" },
        ],
        summary: cleaningGap >= 0
          ? `Your cleaning fee covers turnover with ${money(cleaningGap, "$", 2)} to spare, and the stay nets ${money(net, "$", 2)} after the ${fmt(n(v, "hostFee", 3), 1)}% host fee.`
          : `Your cleaning fee falls ${money(Math.abs(cleaningGap), "$", 2)} short of the real turnover cost, so nightly revenue is quietly subsidising cleaning.`,
        table: {
          title: "Per-stay P&L",
          head: ["Line", "Amount"],
          rows: [
            ["Nightly revenue", money(nightly * nights, "$", 2)],
            ["Cleaning fee collected", money(feeCharged, "$", 2)],
            ["Gross booking value", money(gross, "$", 2)],
            ["Host platform fee", `− ${money(platform, "$", 2)}`],
            ["Cleaner payment", `− ${money(cleaner, "$", 2)}`],
            ["Supplies restock", `− ${money(supplies, "$", 2)}`],
            ["Host net profit", money(net, "$", 2)],
            ["Net margin", `${fmt(margin, 1)}%`],
          ],
        },
        bars: [1, 2, 3, 5, 7].map((k) => {
          const g = nightly * k + feeCharged;
          const nt = g - g * hostFee - turnover;
          return { label: `${k}-night stay`, value: Math.max(0, nt), display: money(nt, "$", 0) };
        }),
      };
    },
    intro:
      "A cleaning fee that looks generous on the listing page can still lose money once you pay the cleaner, restock consumables and hand the platform its cut. This calculator shows the true per-stay economics so you can price the fee deliberately instead of copying the host down the street.",
    sections: [
      { h: "Why short stays destroy margin", body: "Turnover cost is fixed per booking while nightly revenue scales with length of stay. A one-night booking pays the same cleaner as a seven-night booking, which is exactly why experienced hosts push minimum-night rules rather than simply raising the cleaning fee." },
      { h: "Guests judge the total, not the fee", body: "Search results increasingly display total price including fees, so an inflated cleaning fee no longer hides the cost — it just makes the listing look badly priced. Many hosts now set the fee at or slightly below true turnover cost and recover the rest in the nightly rate." },
      { h: "What belongs in turnover cost", body: "Cleaner payment, laundry, consumables such as coffee, soap, paper goods and dishwasher tabs, plus a small amortised allowance for linen replacement. Ignoring consumables is the most common reason hosts think they are more profitable than they are." },
      { h: "The 3% host service fee", body: "Most hosts on the split-fee structure pay around 3% of the total booking value, charged on the cleaning fee as well as the nightly rate. Simplified pricing plans can push this to 14-16%, so change the percentage field to match your own plan." },
      { h: "Turning the number into a pricing rule", body: "Decide a target net margin — 55-70% before mortgage and utilities is a common benchmark — then adjust nightly rate first and cleaning fee second, since the nightly rate is what drives search ranking and perceived value." },
    ],
    staticTable: {
      title: "Typical US short-term rental turnover costs",
      head: ["Property size", "Cleaner pay", "Supplies", "Common fee charged"],
      rows: [
        ["Studio", "$35–$55", "$6–$10", "$45–$65"],
        ["1 bedroom", "$50–$75", "$8–$14", "$60–$85"],
        ["2 bedroom", "$70–$110", "$12–$20", "$85–$125"],
        ["3 bedroom", "$95–$150", "$16–$28", "$110–$170"],
        ["4+ bedroom / with pool", "$140–$250", "$25–$45", "$160–$275"],
      ],
      caption: "Metro markets run at the top of each band; rural markets at the bottom.",
    },
    faqs: [
      { q: "Should the cleaning fee exactly match what I pay the cleaner?", a: "Match it to cleaner pay plus consumables. Charging exactly the cleaner's invoice leaves supplies unfunded, which silently reduces margin on every stay." },
      { q: "Is a high cleaning fee bad for bookings?", a: "It can be. Since platforms show total price in search, an above-market fee lowers your effective ranking and conversion even when your nightly rate is competitive." },
      { q: "How do I make one-night stays profitable?", a: "Either set a minimum of two nights, add a short-stay surcharge on the nightly rate, or accept them only in low-occupancy windows where any margin beats an empty night." },
      { q: "Does the host service fee apply to the cleaning fee too?", a: "Yes. Platform commission is normally calculated on the total booking value, cleaning fee included, which this calculator reflects." },
      { q: "What net margin should I target per stay?", a: "Before mortgage, utilities and taxes, many hosts aim for 55-70%. Anything below 40% usually signals underpricing or an overpaying turnover arrangement." },
      { q: "Should I include laundry in supplies?", a: "Yes if you outsource it or run machines yourself. Add the per-turnover cost, including detergent and electricity, into the supplies field." },
      { q: "How do longer stays change the maths?", a: "Dramatically. Spreading one fixed turnover cost over seven nights instead of two can add 15-20 percentage points to net margin, which the stay-length chart illustrates." },
      { q: "Do guests expect chores if I charge a cleaning fee?", a: "Increasingly yes — guests resent long checkout task lists alongside a large fee. Keep checkout requests to bins, dishes and keys." },
      { q: "How often should I re-run this?", a: "Any time your cleaner raises rates, you change platform pricing plans, or supply costs shift — realistically twice a year." },
      { q: "Can I use this for Vrbo or Booking.com?", a: "Yes. Just change the platform fee percentage to match that channel's commission, which is often materially higher than 3%." },
    ],
    formula: {
      expressions: [
        "Gross = (Nightly_Rate × Nights) + Cleaning_Fee_Charged",
        "Platform_Fee = Gross × Host_Fee%",
        "Turnover_Cost = Cleaner_Fee + Supplies",
        "Net_Profit = Gross − Platform_Fee − Turnover_Cost",
        "Net_Margin% = (Net_Profit / Gross) × 100",
      ],
      variables: [
        { name: "Host_Fee%", meaning: "Platform commission, typically 3% on split-fee pricing" },
        { name: "Turnover_Cost", meaning: "Everything spent resetting the property between guests" },
      ],
    },
    related: ["overtime-pay-calculator", "stripe-vs-paypal-fee-calculator", "ecommerce-return-loss-calculator", "side-hustle-profit-calculator", "rent-splitter-by-square-feet"],
  },

  {
    slug: "overtime-pay-calculator",
    cluster: "finance",
    name: "Overtime Pay 1.5x / 2.0x",
    h1: "Overtime Pay Calculator: Time-and-a-Half (1.5x) & Double Time (2.0x)",
    title: "Overtime Pay Calculator — Time and a Half & Double Time (2026)",
    description:
      "Calculate time-and-a-half and double-time overtime pay with tax withholding. See gross regular pay, overtime pay, holiday premium pay and net take-home in seconds.",
    keywords: [
      "time and a half calculator overtime pay",
      "calculate double time hourly wage",
      "overtime tax calculator net check",
      "1.5x overtime rate per hour calculator",
      "holiday double time pay calculator",
      "weekly paycheck with overtime estimator",
    ],
    inputs: [
      { id: "rate", label: "Base hourly rate", type: "number", def: "22", unit: "$", min: 0, step: 0.25 },
      { id: "reg", label: "Regular hours (up to 40)", type: "number", def: "40", unit: "hrs", min: 0, max: 40, step: 0.5 },
      { id: "ot", label: "Overtime hours (1.5x)", type: "number", def: "8", unit: "hrs", min: 0, step: 0.5 },
      { id: "dt", label: "Double time hours (2.0x)", type: "number", def: "4", unit: "hrs", min: 0, step: 0.5 },
      { id: "tax", label: "Estimated tax withholding", type: "number", def: "22", unit: "%", min: 0, max: 60, step: 0.5 },
    ],
    compute: (v) => {
      const rate = n(v, "rate", 22), reg = n(v, "reg", 40), ot = n(v, "ot", 8), dt = n(v, "dt", 4);
      const tax = n(v, "tax", 22) / 100;
      const regPay = rate * reg, otPay = rate * 1.5 * ot, dtPay = rate * 2 * dt;
      const gross = regPay + otPay + dtPay;
      const withheld = gross * tax;
      const net = gross - withheld;
      const hours = reg + ot + dt;
      const blended = hours > 0 ? gross / hours : 0;
      return {
        cards: [
          { label: "Gross regular pay", value: money(regPay, "$", 2), sub: `${fmt(reg, 1)} hrs @ ${money(rate, "$", 2)}` },
          { label: "Overtime pay (1.5x)", value: money(otPay, "$", 2), sub: `${fmt(ot, 1)} hrs @ ${money(rate * 1.5, "$", 2)}` },
          { label: "Double time pay (2.0x)", value: money(dtPay, "$", 2), sub: `${fmt(dt, 1)} hrs @ ${money(rate * 2, "$", 2)}` },
          { label: "Take-home net pay", value: money(net, "$", 2), sub: `${money(gross, "$", 2)} gross − ${fmt(n(v, "tax", 22), 1)}% withheld`, accent: true },
        ],
        summary: `${fmt(hours, 1)} paid hours produce ${money(gross, "$", 2)} gross and about ${money(net, "$", 2)} in your bank, a blended rate of ${money(blended, "$", 2)} per hour.`,
        table: {
          title: "Pay breakdown",
          head: ["Category", "Hours", "Rate", "Gross"],
          rows: [
            ["Regular", fmt(reg, 1), money(rate, "$", 2), money(regPay, "$", 2)],
            ["Overtime 1.5x", fmt(ot, 1), money(rate * 1.5, "$", 2), money(otPay, "$", 2)],
            ["Double time 2.0x", fmt(dt, 1), money(rate * 2, "$", 2), money(dtPay, "$", 2)],
            ["Total gross", fmt(hours, 1), money(blended, "$", 2), money(gross, "$", 2)],
            ["Withholding", "—", `${fmt(n(v, "tax", 22), 1)}%`, `− ${money(withheld, "$", 2)}`],
            ["Net take-home", "—", "—", money(net, "$", 2)],
          ],
        },
        bars: [
          { label: "Regular", value: regPay, display: money(regPay, "$", 0) },
          { label: "Overtime", value: otPay, display: money(otPay, "$", 0) },
          { label: "Double time", value: dtPay, display: money(dtPay, "$", 0) },
        ],
      };
    },
    intro:
      "Overtime maths looks simple until a pay period mixes regular hours, time-and-a-half and holiday double time, and withholding lands differently than expected. Enter your rate and hours and this calculator separates each premium tier, then shows the net figure you will actually see on the cheque.",
    sections: [
      { h: "How time-and-a-half is legally calculated", body: "Under the US Fair Labor Standards Act, non-exempt employees earn at least 1.5 times their regular rate for hours worked beyond 40 in a workweek. The regular rate is not always the base rate — non-discretionary bonuses and shift differentials must be folded in before multiplying." },
      { h: "When double time applies", body: "Federal law does not mandate double time. It comes from state rules such as California's over-12-hours-in-a-day provision, union agreements, or company holiday policy. Always check which hours your employer classifies as 2.0x before assuming the premium." },
      { h: "Why overtime feels over-taxed", body: "Overtime is taxed at exactly the same rates as regular income, but payroll systems annualise a large cheque and withhold as if every period looked like that one. The excess comes back at filing time, which is why the withholding percentage here is an estimate, not a final tax bill." },
      { h: "Blended rate matters for planning", body: "The blended figure — total gross divided by total hours — tells you what your time is actually worth this period. Comparing it against a second job or contract work is more honest than comparing base rates." },
      { h: "Daily versus weekly overtime rules", body: "Some states calculate overtime daily (over 8 hours in a day) rather than weekly. If you work four ten-hour days, weekly rules give you no overtime while daily rules give you eight premium hours, so enter hours according to the rule your state uses." },
    ],
    staticTable: {
      title: "Premium rates at common base wages",
      head: ["Base rate", "1.5x rate", "2.0x rate", "8 OT hrs gross"],
      rows: [
        ["$15.00", "$22.50", "$30.00", "$180.00"],
        ["$18.00", "$27.00", "$36.00", "$216.00"],
        ["$22.00", "$33.00", "$44.00", "$264.00"],
        ["$28.00", "$42.00", "$56.00", "$336.00"],
        ["$35.00", "$52.50", "$70.00", "$420.00"],
        ["$45.00", "$67.50", "$90.00", "$540.00"],
      ],
    },
    faqs: [
      { q: "Is overtime taxed at a higher rate?", a: "No. The income tax rate is identical; only the withholding calculation temporarily assumes a higher annual income, so more is held back and reconciled at tax time." },
      { q: "Does time-and-a-half start after 8 hours or 40?", a: "Federally it starts after 40 hours in a workweek. Several states, notably California, also apply daily overtime after 8 hours." },
      { q: "Do paid holidays count toward the 40-hour threshold?", a: "Usually no. Overtime is generally based on hours actually worked, so holiday or PTO hours often do not trigger the premium unless your contract says otherwise." },
      { q: "What is the regular rate of pay?", a: "Total straight-time earnings for the week — including non-discretionary bonuses and shift differentials — divided by hours worked. It can exceed the posted base rate." },
      { q: "Can salaried employees earn overtime?", a: "Yes, if they are classified as non-exempt. Exempt status requires meeting salary threshold and duties tests, not simply being paid a salary." },
      { q: "Is double time required on holidays?", a: "Not by federal law. Holiday premium pay is a matter of employer policy or collective agreement." },
      { q: "How do I estimate my withholding percentage?", a: "Divide the total deductions on a recent payslip by that payslip's gross pay. Include federal, state, Social Security and Medicare for a realistic figure." },
      { q: "Does overtime affect my 401(k) contribution?", a: "If your contribution is a percentage of gross pay, yes — a bigger cheque means a bigger contribution, which slightly reduces net take-home beyond taxes." },
      { q: "Can my employer average hours across two weeks?", a: "Generally no for standard non-exempt employees; each workweek stands alone. Limited exceptions exist for healthcare and public safety schedules." },
      { q: "What if I work overtime at two different rates?", a: "Run the calculator once per rate and add the results, or use a weighted average rate as the base if your employer uses the blended-rate method." },
    ],
    formula: {
      expressions: [
        "Regular_Pay = Base × Regular_Hours",
        "Overtime_Pay = Base × 1.5 × OT_Hours",
        "DoubleTime_Pay = Base × 2.0 × DT_Hours",
        "Total_Gross = Regular_Pay + Overtime_Pay + DoubleTime_Pay",
        "Net_Pay = Total_Gross × (1 − Tax%)",
      ],
      variables: [
        { name: "Base", meaning: "Regular hourly rate of pay" },
        { name: "Tax%", meaning: "Combined estimated withholding rate" },
      ],
    },
    related: ["hourly-to-annual-salary-converter", "freelance-hourly-rate-calculator", "airbnb-cleaning-fee-calculator", "side-hustle-profit-calculator", "pawn-loan-apr-calculator"],
  },

  {
    slug: "stripe-vs-paypal-fee-calculator",
    cluster: "finance",
    name: "Stripe vs PayPal Fees",
    h1: "Stripe vs PayPal Fee Calculator: Cross-Border & FX Conversion",
    title: "Stripe vs PayPal Fee Calculator — Cross-Border & FX (2026)",
    description:
      "Compare Stripe and PayPal payout after processing, international card and currency conversion fees. See exactly which processor leaves more money in your account.",
    keywords: [
      "stripe international card fee calculator",
      "paypal currency conversion fee breakdown",
      "stripe vs paypal payout calculator",
      "cross border card processing fee comparison",
      "how much does paypal take from 1000 dollars",
      "stripe 2.9 percent plus 30 cents calculator",
    ],
    inputs: [
      { id: "amount", label: "Transaction amount", type: "number", def: "500", unit: "$", min: 0.5, step: 1 },
      {
        id: "origin", label: "Sender country", type: "select", def: "domestic",
        options: [
          { value: "domestic", label: "Domestic card" },
          { value: "intl", label: "International card" },
        ],
      },
      {
        id: "fx", label: "Currency conversion needed?", type: "select", def: "no",
        options: [
          { value: "no", label: "No — same currency" },
          { value: "yes", label: "Yes — converted to my currency" },
        ],
      },
    ],
    compute: (v) => {
      const amt = n(v, "amount", 500);
      const intl = s(v, "origin", "domestic") === "intl";
      const fx = s(v, "fx", "no") === "yes";
      const stripePct = 0.029 + (intl ? 0.015 : 0) + (fx ? 0.01 : 0);
      const paypalPct = 0.0349 + (intl ? 0.015 : 0) + (fx ? 0.035 : 0);
      const stripeFee = amt * stripePct + 0.3;
      const paypalFee = amt * paypalPct + 0.49;
      const stripeNet = amt - stripeFee, paypalNet = amt - paypalFee;
      const diff = stripeNet - paypalNet;
      return {
        cards: [
          { label: "Stripe net payout", value: money(stripeNet, "$", 2), sub: `Fee ${money(stripeFee, "$", 2)} (${fmt(stripePct * 100, 2)}% + $0.30)`, accent: diff >= 0 },
          { label: "PayPal net payout", value: money(paypalNet, "$", 2), sub: `Fee ${money(paypalFee, "$", 2)} (${fmt(paypalPct * 100, 2)}% + $0.49)`, accent: diff < 0 },
          { label: "Savings difference", value: money(Math.abs(diff), "$", 2), sub: diff >= 0 ? "Stripe keeps more in your pocket" : "PayPal keeps more in your pocket" },
          { label: "Effective fee rate", value: `${fmt((Math.min(stripeFee, paypalFee) / amt) * 100, 2)}%`, sub: "Cheaper processor, all-in" },
        ],
        summary: `On a ${money(amt, "$", 2)} ${intl ? "international" : "domestic"} payment${fx ? " with currency conversion" : ""}, ${diff >= 0 ? "Stripe" : "PayPal"} nets you ${money(Math.abs(diff), "$", 2)} more.`,
        table: {
          title: "Fee stack comparison",
          head: ["Component", "Stripe", "PayPal"],
          rows: [
            ["Base percentage", "2.90%", "3.49%"],
            ["Fixed fee", "$0.30", "$0.49"],
            ["International card", intl ? "+1.50%" : "—", intl ? "+1.50%" : "—"],
            ["Currency conversion", fx ? "+1.00%" : "—", fx ? "+3.50%" : "—"],
            ["Total fee", money(stripeFee, "$", 2), money(paypalFee, "$", 2)],
            ["Net payout", money(stripeNet, "$", 2), money(paypalNet, "$", 2)],
          ],
        },
        bars: [
          { label: "Stripe net", value: stripeNet, display: money(stripeNet, "$", 2) },
          { label: "PayPal net", value: paypalNet, display: money(paypalNet, "$", 2) },
        ],
      };
    },
    intro:
      "Headline rates of 2.9% and 3.49% hide the part that actually decides your payout: cross-border surcharges and currency conversion spreads. Enter one transaction and this calculator stacks every layer for both processors side by side.",
    sections: [
      { h: "Where the real gap opens up", body: "For a domestic same-currency sale the two processors differ by well under a percentage point. Add a foreign card and a currency conversion and PayPal's 3.5% FX spread against Stripe's roughly 1% turns a rounding error into real money — often 2-3% of every international sale." },
      { h: "Fixed fees punish small transactions", body: "A $0.49 fixed component on a $5 payment is nearly 10% before any percentage applies. If your average order value is low, the fixed fee, not the percentage, is the number to optimise — which is also the argument for bundling or minimum order values." },
      { h: "Currency conversion is a hidden spread", body: "Conversion charges are applied to the mid-market rate as a margin, so they never appear as a separate line on your payout. The only way to see them is to compare the received amount against the mid-market rate on the settlement date." },
      { h: "When PayPal still wins", body: "Checkout conversion matters more than fees for many stores. If offering PayPal lifts completed checkouts by a few percentage points, that gain can comfortably exceed a 1-2% fee difference — which is why many merchants offer both and route by geography." },
      { h: "Practical ways to cut cross-border cost", body: "Hold a balance in the sending currency where the processor supports it, settle to a multi-currency account, price in the customer's local currency to avoid double conversion, and negotiate custom rates once monthly volume passes roughly $80k." },
    ],
    staticTable: {
      title: "Published rate components (US merchant accounts)",
      head: ["Component", "Stripe", "PayPal"],
      rows: [
        ["Domestic card", "2.9% + $0.30", "3.49% + $0.49"],
        ["International card surcharge", "+1.5%", "+1.5%"],
        ["Currency conversion", "+1.0%", "+3.5% (approx)"],
        ["Chargeback fee", "$15", "$20"],
        ["Refund fixed fee", "Not returned", "Not returned"],
        ["Payout timing", "2 days rolling", "Instant to 1 day"],
      ],
      caption: "Rates are standard published US pricing and change periodically; verify before large decisions.",
    },
    faqs: [
      { q: "How much does PayPal take from $1,000?", a: "On a domestic same-currency sale, roughly $35.39 (3.49% plus $0.49). Add international and FX layers and it can exceed $85." },
      { q: "Is Stripe always cheaper than PayPal?", a: "On raw fees for card payments, usually yes — especially with currency conversion. But PayPal can win on checkout conversion and on buyer trust in some markets." },
      { q: "What counts as an international card?", a: "A card issued by a bank outside your merchant account's country. The surcharge is triggered by card issuance country, not the customer's shipping address." },
      { q: "Can I avoid the FX fee entirely?", a: "Sometimes. Both processors let you settle into a bank account held in the payment currency, which avoids conversion at the processor level." },
      { q: "Are the fixed fees refunded on a refund?", a: "Generally no. Both processors keep the fixed component when you refund a sale, so high-refund businesses pay a hidden fee on every reversal." },
      { q: "Do these rates apply to invoices and subscriptions?", a: "Subscription billing typically uses the same card rate; invoicing products often add a small extra percentage, so check the specific product page." },
      { q: "Which is better for a low-ticket digital product?", a: "Stripe, almost always, because the lower fixed fee dominates when the transaction value is small." },
      { q: "Can I negotiate lower rates?", a: "Yes. Both processors offer custom interchange-plus pricing once monthly volume is substantial, commonly discussed from around $80k per month." },
      { q: "Do these figures include payout or withdrawal fees?", a: "No. Instant payouts and cross-border bank withdrawals carry additional charges on both platforms." },
      { q: "How do chargebacks compare?", a: "Stripe charges around $15 per dispute and PayPal around $20, with both returning the fee if you win the dispute." },
    ],
    formula: {
      expressions: [
        "Stripe_Fee = Amount × (2.9% + Intl 1.5% + FX 1.0%) + $0.30",
        "PayPal_Fee = Amount × (3.49% + Intl 1.5% + FX 3.5%) + $0.49",
        "Net_Payout = Amount − Processor_Fee",
        "Savings = Stripe_Net − PayPal_Net",
      ],
      variables: [
        { name: "Intl", meaning: "Applied only when the card was issued abroad" },
        { name: "FX", meaning: "Applied only when funds are converted to another currency" },
      ],
    },
    related: ["substack-patreon-fee-calculator", "ecommerce-return-loss-calculator", "airbnb-cleaning-fee-calculator", "side-hustle-profit-calculator", "etsy-shipping-margin-calculator"],
  },

  {
    slug: "ecommerce-return-loss-calculator",
    cluster: "finance",
    name: "E-Commerce Return Loss",
    h1: "E-Commerce Return Rate Loss & Refund Profit Erosion Calculator",
    title: "E-Commerce Return Rate Loss Calculator — Refund Impact (2026)",
    description:
      "Measure what returns really cost: returned order count, lost revenue, reverse-logistics spend and the net margin hit across a month of shipped orders.",
    keywords: [
      "e-commerce return rate financial impact calculator",
      "shopify refund profit loss estimator",
      "cost of returns calculation model",
      "how much do returns cost my store",
      "reverse logistics cost per return calculator",
      "return rate margin erosion tool",
    ],
    inputs: [
      { id: "orders", label: "Monthly shipped orders", type: "number", def: "1200", unit: "orders", min: 1, step: 10 },
      { id: "aov", label: "Average order value (AOV)", type: "number", def: "68", unit: "$", min: 1, step: 1 },
      { id: "margin", label: "Gross profit margin", type: "number", def: "42", unit: "%", min: 1, max: 95, step: 0.5 },
      { id: "rate", label: "Return rate", type: "number", def: "12", unit: "%", min: 0, max: 80, step: 0.5 },
      { id: "returnCost", label: "Shipping + restock cost per return", type: "number", def: "9", unit: "$", min: 0, step: 0.5 },
      { id: "scrap", label: "Share of returns unsellable", type: "number", def: "15", unit: "%", min: 0, max: 100, step: 1 },
    ],
    compute: (v) => {
      const orders = n(v, "orders", 1200), aov = n(v, "aov", 68);
      const marginPct = n(v, "margin", 42) / 100, ratePct = n(v, "rate", 12) / 100;
      const returnCost = n(v, "returnCost", 9), scrapPct = n(v, "scrap", 15) / 100;
      const returned = orders * ratePct;
      const revenueLost = returned * aov;
      const logistics = returned * returnCost;
      const cogsPerUnit = aov * (1 - marginPct);
      const scrapped = returned * scrapPct * cogsPerUnit;
      const totalLoss = logistics + scrapped;
      const grossRevenue = orders * aov;
      const netRevenue = grossRevenue - revenueLost;
      const profitBefore = grossRevenue * marginPct;
      const profitAfter = netRevenue * marginPct - totalLoss;
      const marginAfter = grossRevenue > 0 ? (profitAfter / grossRevenue) * 100 : 0;
      const impact = marginPct * 100 - marginAfter;
      return {
        cards: [
          { label: "Returned orders / month", value: fmt(returned, 0), sub: `${fmt(ratePct * 100, 1)}% of ${fmt(orders, 0)} shipped` },
          { label: "Lost gross revenue", value: money(revenueLost, "$", 0), sub: "Refunded order value" },
          { label: "Return fulfilment losses", value: money(totalLoss, "$", 0), sub: `${money(logistics, "$", 0)} logistics + ${money(scrapped, "$", 0)} scrapped stock`, accent: true },
          { label: "Net margin impact", value: `−${fmt(impact, 1)} pts`, sub: `${fmt(marginPct * 100, 1)}% → ${fmt(marginAfter, 1)}% effective` },
        ],
        summary: `Returns pull about ${money(totalLoss + revenueLost * marginPct, "$", 0)} of profit out of the month and drop your effective margin from ${fmt(marginPct * 100, 1)}% to ${fmt(marginAfter, 1)}%.`,
        table: {
          title: "Monthly return economics",
          head: ["Metric", "Value"],
          rows: [
            ["Gross revenue shipped", money(grossRevenue, "$", 0)],
            ["Returned units", fmt(returned, 0)],
            ["Refunded revenue", `− ${money(revenueLost, "$", 0)}`],
            ["Reverse logistics cost", `− ${money(logistics, "$", 0)}`],
            ["Scrapped / unsellable COGS", `− ${money(scrapped, "$", 0)}`],
            ["Profit before returns", money(profitBefore, "$", 0)],
            ["Profit after returns", money(profitAfter, "$", 0)],
            ["Effective margin", `${fmt(marginAfter, 1)}%`],
          ],
        },
        bars: [4, 8, 12, 20, 30].map((r) => {
          const ret = orders * (r / 100);
          const loss = ret * returnCost + ret * scrapPct * cogsPerUnit + ret * aov * marginPct;
          return { label: `${r}% return rate`, value: loss, display: money(loss, "$", 0) };
        }),
      };
    },
    intro:
      "A refund does not just reverse a sale — it deletes the margin, pays for a second shipping leg, ties up labour and often writes off the unit. This calculator turns your return rate into the monthly profit figure it really represents.",
    sections: [
      { h: "Returns cost more than the refund", body: "For every returned order you lose the gross profit, pay inbound shipping, pay someone to inspect and restock, and write off a share of units that cannot be resold as new. Stores that only track refunded revenue routinely understate the true cost by half." },
      { h: "Why apparel is the hardest category", body: "Fashion return rates commonly sit between 20% and 40% because fit is uncertain at purchase, while electronics and consumables generally sit under 10%. Higher rates make sizing tools, detailed measurement charts and fit reviews some of the highest-ROI spend available." },
      { h: "The unsellable share is the silent killer", body: "Opened cosmetics, worn shoes, damaged packaging and seasonal stock returned after the window often cannot go back to full price. Even a 15% scrap rate on returns converts directly into written-off COGS with no revenue attached." },
      { h: "Free returns are a marketing expense", body: "Free returns lift conversion, so treat their cost as customer acquisition rather than as pure loss. Compare the incremental conversion gain against the reverse-logistics line in this calculator before removing the policy." },
      { h: "Levers that actually reduce the number", body: "Accurate sizing data, more real photography, customer-submitted fit reviews, better packaging to prevent transit damage, and flagging serial returners all measurably reduce return rate. Even shaving two percentage points shifts the monthly figure noticeably." },
    ],
    staticTable: {
      title: "Typical return rates by category",
      head: ["Category", "Online return rate", "Common driver"],
      rows: [
        ["Apparel & footwear", "20–40%", "Fit and sizing"],
        ["Jewellery & accessories", "10–18%", "Appearance vs photos"],
        ["Consumer electronics", "8–12%", "Compatibility"],
        ["Home & furniture", "8–15%", "Scale and damage"],
        ["Beauty & cosmetics", "4–8%", "Shade mismatch"],
        ["Consumables & supplements", "2–5%", "Rare, mostly damage"],
      ],
    },
    faqs: [
      { q: "What is a healthy e-commerce return rate?", a: "It is category dependent. Under 10% is strong for most general merchandise, while apparel stores often consider 20% acceptable and 30%+ a problem." },
      { q: "Should I include the original outbound shipping in the loss?", a: "Yes if you paid it and cannot recover it. Add it to the per-return cost field alongside the inbound label and handling time." },
      { q: "How do I estimate unsellable percentage?", a: "Audit one month of returns and record how many went back to full-price stock versus outlet, refurb or bin. That ratio is usually more pessimistic than owners expect." },
      { q: "Do returns hurt more than discounts?", a: "Often yes. A 20% discount keeps the sale and the customer; a return loses the margin, adds handling cost and may cost the unit as well." },
      { q: "Does charging for returns help?", a: "It reliably reduces return rate but also reduces conversion. Test on a segment before rolling it out, and measure net profit rather than return rate alone." },
      { q: "How should I treat restocking labour?", a: "Convert it to a per-unit cost — minutes spent times the loaded hourly wage — and include it in the per-return cost input." },
      { q: "Is the margin impact figure before or after ad spend?", a: "Before. It compares gross margin to gross margin so you can isolate the return effect, then apply your marketing costs on top." },
      { q: "Can returns ever be profitable?", a: "Indirectly. A smooth returns experience raises repeat purchase rates, so cohorts with an easy first return often show higher lifetime value." },
      { q: "How often should I re-run this model?", a: "Monthly alongside your P&L, and immediately after any policy, packaging or sizing change so you can attribute the shift." },
      { q: "Does this work for marketplace sellers?", a: "Yes — add the marketplace's non-refunded commission to the per-return cost, since most platforms retain part of their fee on refunded orders." },
    ],
    formula: {
      expressions: [
        "Returned_Units = Orders × (Return_Rate% / 100)",
        "Revenue_Lost = Returned_Units × AOV",
        "Logistics_Cost = Returned_Units × Cost_per_Return",
        "Scrapped_COGS = Returned_Units × Unsellable% × (AOV × (1 − Margin%))",
        "Net_Loss = Logistics_Cost + Scrapped_COGS",
      ],
      variables: [
        { name: "AOV", meaning: "Average order value" },
        { name: "Unsellable%", meaning: "Share of returned units that cannot be resold at full price" },
      ],
    },
    related: ["stripe-vs-paypal-fee-calculator", "etsy-shipping-margin-calculator", "airbnb-cleaning-fee-calculator", "side-hustle-profit-calculator", "pawn-loan-apr-calculator"],
  },

  {
    slug: "pawn-loan-apr-calculator",
    cluster: "finance",
    name: "Pawn & Title Loan True APR",
    h1: "Pawn Shop & Car Title Loan True APR Interest Rate Calculator",
    title: "Pawn Shop & Title Loan APR Calculator — True Interest (2026)",
    description:
      "Convert pawn shop or car title loan fees into a true annual percentage rate. See total finance charges, the periodic rate and the real APR behind a short-term loan.",
    keywords: [
      "pawn shop interest rate apr calculator",
      "car title loan yearly interest calculator",
      "true apr formula for monthly pawn fees",
      "convert monthly loan fee to apr",
      "title loan 25 percent monthly apr",
      "short term loan real cost calculator",
    ],
    inputs: [
      { id: "principal", label: "Principal loan amount", type: "number", def: "500", unit: "$", min: 1, step: 10 },
      { id: "finance", label: "Finance / interest fee", type: "number", def: "125", unit: "$", min: 0, step: 1 },
      { id: "admin", label: "Admin / storage / lien fees", type: "number", def: "25", unit: "$", min: 0, step: 1 },
      { id: "duration", label: "Loan duration", type: "number", def: "30", unit: "days", min: 1, step: 1 },
      { id: "rollovers", label: "Expected rollovers", type: "number", def: "0", unit: "cycles", min: 0, max: 12, step: 1 },
    ],
    compute: (v) => {
      const principal = Math.max(1, n(v, "principal", 500));
      const finance = n(v, "finance", 125), admin = n(v, "admin", 25);
      const days = Math.max(1, n(v, "duration", 30));
      const rolls = n(v, "rollovers", 0);
      const perCycle = finance + admin;
      const totalFees = perCycle * (1 + rolls);
      const totalDays = days * (1 + rolls);
      const periodic = (perCycle / principal) * 100;
      const apr = (perCycle / principal) * (365 / days) * 100;
      const payoff = principal + totalFees;
      return {
        cards: [
          { label: "Total finance charges", value: money(totalFees, "$", 2), sub: rolls > 0 ? `${rolls + 1} cycles over ${fmt(totalDays, 0)} days` : `Single ${fmt(days, 0)}-day term` },
          { label: "Periodic rate", value: `${fmt(periodic, 2)}%`, sub: `Per ${fmt(days, 0)}-day cycle` },
          { label: "True APR", value: `${fmt(apr, 1)}%`, sub: "Annualised cost of borrowing", accent: true },
          { label: "Total payoff", value: money(payoff, "$", 2), sub: `On ${money(principal, "$", 0)} borrowed` },
        ],
        summary: `A ${money(perCycle, "$", 2)} charge on ${money(principal, "$", 0)} for ${fmt(days, 0)} days is ${fmt(periodic, 2)}% per cycle — an annual percentage rate of about ${fmt(apr, 1)}%.`,
        table: {
          title: "Cost breakdown",
          head: ["Item", "Amount"],
          rows: [
            ["Principal borrowed", money(principal, "$", 2)],
            ["Finance fee per cycle", money(finance, "$", 2)],
            ["Admin / storage fee per cycle", money(admin, "$", 2)],
            ["Cycles (including rollovers)", fmt(1 + rolls, 0)],
            ["Total fees paid", money(totalFees, "$", 2)],
            ["Total repayment", money(payoff, "$", 2)],
            ["Periodic rate", `${fmt(periodic, 2)}%`],
            ["True APR", `${fmt(apr, 1)}%`],
          ],
        },
        bars: [0, 1, 2, 3].map((r) => {
          const t = perCycle * (1 + r);
          return { label: r === 0 ? "No rollover" : `${r} rollover${r > 1 ? "s" : ""}`, value: t, display: money(t, "$", 0) };
        }),
      };
    },
    intro:
      "Pawn and title lenders quote a fee, never a rate, because a $125 charge sounds far smaller than 300% APR — even when they are the same thing. Enter the loan and this calculator converts every fee into the annualised number a bank would be forced to disclose.",
    sections: [
      { h: "Why a monthly fee becomes a triple-digit APR", body: "APR annualises the cost of borrowing so different products can be compared honestly. A 25% charge for one month is not 25% a year — repeated twelve times it is roughly 300%, which is the number to weigh against a credit card cash advance at 25-30%." },
      { h: "Rollovers are where the damage happens", body: "The industry earns most of its revenue from borrowers who cannot repay at the first due date and pay the fee again to extend. Three rollovers on a 30-day loan means paying four fees for the use of the same principal, often exceeding the amount borrowed." },
      { h: "Title loans risk the vehicle, pawn loans risk the item", body: "A pawn loan is non-recourse: default and you lose the pledged item but owe nothing further. A title loan is secured against a vehicle you keep driving, which means default can cost transport, and in some states a deficiency balance can survive repossession." },
      { h: "Fees that are easy to miss", body: "Lien recording, storage, insurance add-ons, processing and late charges all belong in the calculation. Entering only the headline interest fee understates APR, sometimes by 30-60 percentage points." },
      { h: "Cheaper alternatives worth pricing first", body: "Credit union payday-alternative loans are capped around 28% APR, employer pay-advance programmes are often free, and even a credit card cash advance at 30% plus a 5% fee is usually an order of magnitude cheaper than a rolled-over title loan." },
    ],
    staticTable: {
      title: "APR comparison for short-term borrowing",
      head: ["Product", "Typical fee", "Effective APR"],
      rows: [
        ["Pawn loan (30 day)", "20–25% per month", "240–300%"],
        ["Car title loan (30 day)", "25% per month", "~300%"],
        ["Payday loan (14 day)", "$15 per $100", "~391%"],
        ["Credit card cash advance", "5% fee + 29.99%", "40–60%"],
        ["Credit union PAL", "$20 application", "~28% cap"],
        ["Personal instalment loan", "Fixed interest", "10–36%"],
      ],
    },
    faqs: [
      { q: "How do I convert a monthly pawn fee to APR?", a: "Divide the fee by the amount borrowed, multiply by 365 divided by the loan term in days, then multiply by 100. A $125 fee on $500 for 30 days is 304% APR." },
      { q: "Is APR the same as interest rate?", a: "No. The interest rate covers only interest, while APR folds in mandatory fees, which is why pawn and title APRs are so much higher than the quoted rate." },
      { q: "Why do lenders quote fees instead of APR?", a: "A dollar fee feels proportionate to the loan, while the annualised percentage exposes how expensive the borrowing really is. Federal disclosure rules require APR on the paperwork even when the sales conversation avoids it." },
      { q: "What happens if I cannot repay a pawn loan?", a: "The shop keeps and sells the pledged item. There is no credit reporting and no further debt, which is the one structural advantage over a title loan." },
      { q: "Can a title lender repossess my car?", a: "Yes, typically after a missed payment and any statutory cure period. Some states also allow the lender to pursue a deficiency balance if the sale does not cover the debt." },
      { q: "Do these loans build credit?", a: "Almost never. Most pawn and title lenders do not report on-time payments, so there is no credit benefit to offset the cost." },
      { q: "Are there APR caps on these loans?", a: "Caps vary widely by state; some cap rates in the 30-36% range while others impose no meaningful limit. Check your state's small-loan statute." },
      { q: "Should I include insurance add-ons in the fees?", a: "Yes, if they are required to get the loan. Any mandatory charge belongs in the fee total for a truthful APR." },
      { q: "Is early repayment cheaper?", a: "Sometimes. Many pawn contracts charge the full cycle fee regardless, but some prorate. Ask before signing and enter the actual charge you would pay." },
      { q: "What is a reasonable APR to accept in an emergency?", a: "Exhaust anything under 36% first — credit union PALs, employer advances, negotiated payment plans — before considering triple-digit products." },
    ],
    formula: {
      expressions: [
        "Total_Fees = Finance_Fee + Admin_Fee",
        "Periodic_Rate% = (Total_Fees / Principal) × 100",
        "APR% = (Total_Fees / Principal) × (365 / Duration_Days) × 100",
        "Total_Payoff = Principal + (Total_Fees × Cycles)",
      ],
      variables: [
        { name: "Duration_Days", meaning: "Length of one loan cycle in days" },
        { name: "Cycles", meaning: "Original term plus each rollover extension" },
      ],
    },
    related: ["overtime-pay-calculator", "debt-snowball-vs-avalanche-calculator", "compound-interest-visualizer", "inflation-purchasing-power-calculator", "ecommerce-return-loss-calculator"],
  },
];